/**
 * @fileoverview Song Review Session Redux Slice
 *
 * Manages song-level review session state including:
 * - Current song being reviewed
 * - Flashcard grades for partial completion
 * - Session progress tracking
 * - Auto-save support via session ID
 *
 * **Session Flow:**
 * 1. User selects song from getSongForReview
 * 2. Session initialized with song metadata and flashcards
 * 3. Grades recorded as user reviews each card
 * 4. Session auto-saved to Convex on grade submission
 * 5. Session completed when all cards reviewed
 *
 * **State Structure:**
 * All state is serializable (primitives and plain objects only) for Redux DevTools and testing.
 *
 * @module store/slices/songReviewSessionSlice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../store'

export type AnkiGrade = 0 | 1 | 2 | 3 | 4 | 5

export interface FlashcardGrade {
  /** Flashcard ID (string for serialization) */
  flashcardId: string
  /** Anki grade (0-5) */
  grade: AnkiGrade
  /** Timestamp when grade was submitted */
  gradedAt: number
}

export interface SongReviewFlashcard {
  /** Flashcard ID (string for serialization) */
  _id: string
  /** Spanish front text */
  frontSpanish: string[]
  /** English back text */
  backEnglish: string[]
  /** Audio timestamp (if available) */
  audioTimestamp?: {
    startTime: number
    endTime: number
    duration: number
    alignmentConfidence?: number
    occurrences?: Array<{ startTime: number; endTime: number }>
  } | null
  /** Anki ease factor */
  easeFactor: number
  /** Anki interval (days) */
  interval: number
  /** Anki repetitions */
  repetitions: number
  /** Due date timestamp */
  dueDate: number
}

export interface SongReviewSessionState {
  /** Whether a review session is active */
  isActive: boolean
  /** Session ID for auto-save (generated on session start) */
  sessionId: string | null
  /** Song ID being reviewed (string for serialization) */
  songId: string | null
  /** Song title */
  songTitle: string | null
  /** Song artist */
  songArtist: string | null
  /** Full song audio URL */
  fullSongUrl: string | null
  /** Flashcards in this song review */
  flashcards: SongReviewFlashcard[]
  /** Current flashcard index (0-based) */
  currentIndex: number
  /** Grades submitted in this session */
  grades: FlashcardGrade[]
  /** Session start timestamp */
  sessionStartTime: number | null
  /** Whether session has been saved to Convex */
  isSaved: boolean
}

const initialState: SongReviewSessionState = {
  isActive: false,
  sessionId: null,
  songId: null,
  songTitle: null,
  songArtist: null,
  fullSongUrl: null,
  flashcards: [],
  currentIndex: 0,
  grades: [],
  sessionStartTime: null,
  isSaved: true,
}

const songReviewSessionSlice = createSlice({
  name: 'songReviewSession',
  initialState,
  reducers: {
    /**
     * Initialize a new song review session
     */
    startSongReviewSession: (
      state,
      action: PayloadAction<{
        sessionId: string
        songId: string
        songTitle: string
        songArtist?: string
        fullSongUrl?: string
        flashcards: SongReviewFlashcard[]
      }>
    ) => {
      state.isActive = true
      state.sessionId = action.payload.sessionId
      state.songId = action.payload.songId
      state.songTitle = action.payload.songTitle
      state.songArtist = action.payload.songArtist || null
      state.fullSongUrl = action.payload.fullSongUrl || null
      state.flashcards = action.payload.flashcards
      state.currentIndex = 0
      state.grades = []
      state.sessionStartTime = Date.now()
      state.isSaved = false
    },

    /**
     * Submit grade for current flashcard and advance
     */
    submitGrade: (
      state,
      action: PayloadAction<{
        flashcardId: string
        grade: AnkiGrade
      }>
    ) => {
      const { flashcardId, grade } = action.payload

      // Record grade
      state.grades.push({
        flashcardId,
        grade,
        gradedAt: Date.now(),
      })

      // Advance to next card if not at end
      if (state.currentIndex < state.flashcards.length - 1) {
        state.currentIndex++
      }

      // Mark as unsaved
      state.isSaved = false
    },

    /**
     * Navigate to specific flashcard index
     */
    setCurrentIndex: (state, action: PayloadAction<number>) => {
      const newIndex = action.payload
      if (newIndex >= 0 && newIndex < state.flashcards.length) {
        state.currentIndex = newIndex
      }
    },

    /**
     * Mark session as saved to Convex
     */
    markSessionSaved: (state) => {
      state.isSaved = true
    },

    /**
     * Complete and clear session
     */
    completeSongReviewSession: (state) => {
      state.isActive = false
      state.sessionId = null
      state.songId = null
      state.songTitle = null
      state.songArtist = null
      state.fullSongUrl = null
      state.flashcards = []
      state.currentIndex = 0
      state.grades = []
      state.sessionStartTime = null
      state.isSaved = true
    },

    /**
     * Resume session from saved state (for interrupted sessions)
     */
    resumeSongReviewSession: (
      state,
      action: PayloadAction<{
        sessionId: string
        songId: string
        songTitle: string
        songArtist?: string
        fullSongUrl?: string
        flashcards: SongReviewFlashcard[]
        currentIndex: number
        grades: FlashcardGrade[]
        sessionStartTime: number
      }>
    ) => {
      state.isActive = true
      state.sessionId = action.payload.sessionId
      state.songId = action.payload.songId
      state.songTitle = action.payload.songTitle
      state.songArtist = action.payload.songArtist || null
      state.fullSongUrl = action.payload.fullSongUrl || null
      state.flashcards = action.payload.flashcards
      state.currentIndex = action.payload.currentIndex
      state.grades = action.payload.grades
      state.sessionStartTime = action.payload.sessionStartTime
      state.isSaved = true
    },

    /**
     * Clear session state (for cancellation)
     */
    clearSongReviewSession: (state) => {
      state.isActive = false
      state.sessionId = null
      state.songId = null
      state.songTitle = null
      state.songArtist = null
      state.fullSongUrl = null
      state.flashcards = []
      state.currentIndex = 0
      state.grades = []
      state.sessionStartTime = null
      state.isSaved = true
    },
  },
})

export const {
  startSongReviewSession,
  submitGrade,
  setCurrentIndex,
  markSessionSaved,
  completeSongReviewSession,
  resumeSongReviewSession,
  clearSongReviewSession,
} = songReviewSessionSlice.actions

export default songReviewSessionSlice.reducer

/** Selectors */
export const selectSongReviewSession = (state: RootState) => state.songReviewSession

export const selectIsSessionActive = (state: RootState) =>
  state.songReviewSession.isActive

export const selectCurrentSong = (state: RootState) => ({
  songId: state.songReviewSession.songId,
  songTitle: state.songReviewSession.songTitle,
  songArtist: state.songReviewSession.songArtist,
  fullSongUrl: state.songReviewSession.fullSongUrl,
})

export const selectCurrentFlashcard = (state: RootState) => {
  const { flashcards, currentIndex } = state.songReviewSession
  return flashcards[currentIndex] || null
}

export const selectSessionProgress = (state: RootState) => {
  const { flashcards, grades } = state.songReviewSession
  return {
    total: flashcards.length,
    reviewed: grades.length,
    percentage: flashcards.length > 0 ? (grades.length / flashcards.length) * 100 : 0,
  }
}

export const selectSessionNeedsSave = (state: RootState) =>
  state.songReviewSession.isActive && !state.songReviewSession.isSaved

export const selectSessionDuration = (state: RootState) => {
  const { sessionStartTime } = state.songReviewSession
  if (!sessionStartTime) return 0
  return Math.floor((Date.now() - sessionStartTime) / 1000) // seconds
}

export const selectAverageGrade = (state: RootState) => {
  const { grades } = state.songReviewSession
  if (grades.length === 0) return 0
  const sum = grades.reduce((acc: number, g: FlashcardGrade) => acc + g.grade, 0)
  return sum / grades.length
}
